import { useTranslation } from 'react-i18next';

import { RadarChart } from './charts';
import { ItemRow } from './ResultRow';
import {
  FatigueBadIcon,
  FatigueBestIcon,
  FatigueHoarseIcon,
  FatigueNormalIcon,
  Pill,
  SectionHeadCard,
} from '../../../../components';
import { conditionRadar, filledAxisCount } from '../../fatigueRadar';

import type { FatigueAxisId } from '../../fatigueRadar';
import type { DiagnosisRecord } from '../../record';

/**
 * 목소리 피로도 (시안 `921:5549`) — **컨디션별로 내 점수가 어떻게 달랐나**.
 *
 * 축 넷은 설문의 `condition` 네 선택지다(최상·보통·살짝 잠김·많이 잠김). 축 값은 그
 * 컨디션이라고 답한 회차들의 종합 점수 평균이다(`conditionRadar`) — 그러니까 이 탭은 한
 * 회차의 사진이 아니라 **이력의 모양**이고, 좋은 날과 나쁜 날의 차이가 곧 "얼마나 쉽게
 * 지치나"다.
 *
 * 축마다 **표본 수를 같이 낸다**. 1회짜리 축과 8회짜리 축이 같은 크기로 그려지는데 둘이
 * 같은 무게일 리 없다 — 숫자 옆에 회차 수가 없으면 화면이 한 번 잰 것을 경향이라고 말한다.
 *
 * 빈 축은 **채우는 방법**을 말한다. 배지에 넣지 않는 이유는 `Pill`이 `whitespace-nowrap`
 * 이고 줄의 오른쪽 칸도 `shrink-0`이라, 긴 문장을 배지에 넣으면 줄이 꺾인다
 * (`ResultRow.tsx` 주석 — 실기기에서 이미 한 번 났다). 배지는 짧게, 문장은 설명 자리로.
 *
 * 탭이 서는 조건(축 둘 이상)은 `visibleTabs`가 갖는다 — 축 하나짜리 레이더는 비교가 아니다.
 */

/**
 * 아이콘은 시안에서 내보낸 것을 그대로 쓴다(`components/icons`는 생성물이라 손대지 않는다).
 * `husky` 축이 `FatigueHoarseIcon`을 쓰는 이유가 그것이다 — 축 id는 설문 어휘를 따라가고,
 * 아이콘 파일 이름은 시안이 내보낸 대로 남는다.
 */
const AXES: { id: FatigueAxisId; icon: typeof FatigueBestIcon }[] = [
  { id: 'best', icon: FatigueBestIcon },
  { id: 'normal', icon: FatigueNormalIcon },
  { id: 'husky', icon: FatigueHoarseIcon },
  { id: 'bad', icon: FatigueBadIcon },
];

export const FatigueSection = ({
  history,
}: {
  /** 저장된 회차 전부. 축 값이 여기서 나온다 — 이 탭만 이력을 본다. */
  history: readonly DiagnosisRecord[];
}) => {
  const { t } = useTranslation();
  const radar = conditionRadar(history);
  const axisAt = (id: FatigueAxisId) => radar.find((a) => a.id === id);
  // 탭 문턱(`visibleTabs`)과 **같은 함수**로 센다 — 따로 세면 갈릴 수 있다.
  const filled = filledAxisCount(radar);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-8 py-4">
        {/* 범례를 안 그린다 — 이 레이더는 평균선을 그리지 않는다(비교할 평균이 없다).
            그려 둔 범례는 화면에 없는 선을 약속하는 문장이다. */}
        <RadarChart
          axes={radar.map((axis) => ({
            id: axis.id,
            label: t(`web.result.fatigue.axis.${axis.id}.label`),
            value: axis.value,
          }))}
          // 점의 위치는 낭독되지 않는다 — 몇 축을 쟀는지는 말로 해야 한다.
          label={t('web.result.fatigue.radarLabel', { filled })}
        />
      </div>

      <div className="flex flex-col gap-8 py-4">
        <SectionHeadCard
          size="md"
          className="mx-[18px]"
          title={t('web.result.tabs.details')}
        />
        <div className="flex flex-col gap-2">
          {AXES.map(({ id, icon: Icon }) => {
            const axis = axisAt(id);
            const measured = axis?.value != null;
            return (
              <ItemRow
                key={id}
                icon={<Icon className="h-5 w-[22px]" aria-hidden="true" />}
                title={t(`web.result.fatigue.axis.${id}.label`)}
                // 잰 축은 그 컨디션이 무엇을 뜻하는지, 빈 축은 채우는 방법을 말한다.
                desc={t(
                  measured
                    ? `web.result.fatigue.axis.${id}.note`
                    : `web.result.fatigue.axis.${id}.prompt`,
                )}
                right={
                  measured ? (
                    <Pill tone="pink">
                      {t('web.result.fatigue.badge', {
                        score: axis?.value,
                        takes: axis?.takes,
                      })}
                    </Pill>
                  ) : (
                    <Pill tone="gray">{t('web.result.fatigue.empty')}</Pill>
                  )
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
